import * as React from "react";
import {FormComponentProps, ValidationRule} from "antd/lib/form";
import {Form} from "antd";
import SelectFormItem from "../common/form-items/SelectFormItem";
import locales from "../locales";
import {getPaymentTypeOptions, PaymentType} from "../common/PaymentType";
import SubmitCancelFormItem from "../common/form-items/SubmitCancelFormItem";
import DatePickerFormItem from "../common/form-items/DatePickerFormItem";
import * as moment from 'moment';
import * as dateUtils from "../utils/date-utils";
import {areMomentsEqualToDay, MOMENT_DATE_FORMAT, toIsoString} from "../utils/date-utils";
import TextAreaFormItem from "../common/form-items/TextAreaFormItem";
import CheckBoxFormItem from "../common/form-items/CheckBoxFormItem";
import {CheckboxChangeEvent} from "antd/lib/checkbox";
import HiddenFormItem from "../common/form-items/HiddenFormItem";
import ClientSelector from "../client-details/ClientSelector";
import {ClientSummary, Invoice} from "../api/dtos/dtos";
import StaticFormItem from "../common/form-items/StaticFormItem";
import InvoiceExternalLink from "./InvoiceExternalLink";
import {renderInvoiceSource, renderInvoiceState, renderInvoiceType} from "./invoice";

const FIELD_CLIENT_ID = 'clientId';
const FIELD_PAYMENT_TYPE = 'paymentType';
const FIELD_CREATED = 'date';
const FIELD_FULFILLMENT = 'fulfillment';
const FIELD_DEADLINE = 'deadline';
const FIELD_COMMENT = 'comment';
const FIELD_CONTINUOUS_FULFILLMENT = 'continuousFulfillment';

export interface InvoiceFormResult {
  clientId: number;
  paymentType: string;
  created: string;
  fulfillment: string;
  deadline: string;
  comment: string;
  continuousFulfillment: boolean;
}

interface Props extends FormComponentProps {
  editable: boolean;
  client?: ClientSummary;
  invoice?: Invoice;
  invoiceComment?: string;
  onFormSubmit: (result: InvoiceFormResult) => void;
  onFormCancel: () => void;
}

interface State {
  client?: ClientSummary;
  fulfillmentRules?: ValidationRule[];
  paymentDeadlineRules?: ValidationRule[];
}

class InvoiceForm extends React.Component<Props, State> {

  public state = {
    client: this.props.client,
    fulfillmentRules: undefined,
    paymentDeadlineRules: undefined
  };

  public render(): React.ReactElement {
    const {form, editable, invoice} = this.props;
    const {fulfillmentRules, paymentDeadlineRules, client} = this.state;
    const commentInitialValue = this.props.invoiceComment;
    const disabled = !editable;

    return (
      <>
        <Form>
          {invoice &&
              <>
                  <StaticFormItem
                      label={locales.id}
                      initialValue={<InvoiceExternalLink invoice={invoice}/>}
                  />
                  <StaticFormItem
                      label={locales.type}
                      initialValue={renderInvoiceType(invoice.type)}
                  />
                  <StaticFormItem
                      label={locales.state}
                      initialValue={renderInvoiceState(invoice.state)}
                  />
                  <StaticFormItem
                      label={locales.source}
                      initialValue={renderInvoiceSource(invoice)}
                  />
                  <StaticFormItem
                      label={locales.last_changed_at}
                      initialValue={dateUtils.formatDateTimeOrNull(invoice.lastChangedAt) || '-'}
                  />
                  <StaticFormItem
                      label={locales.last_changed_by}
                      initialValue={invoice.lastChangedBy || '-'}
                  />
              </>
          }

          <HiddenFormItem
            form={form}
            fieldId={FIELD_CLIENT_ID}
            label={locales.client}
            initialValue={client ? client.id : null}
            required={true}
          >
            <ClientSelector
              client={client}
              onSelect={this.onClientSelect}
              editable={this.props.client === undefined}
            />
          </HiddenFormItem>

          <SelectFormItem
            form={form}
            fieldId={FIELD_PAYMENT_TYPE}
            label={locales.paymentType}
            initialValue={invoice ? invoice.paymentType : PaymentType.TRANSFER}
            required={true}
            options={getPaymentTypeOptions()}
            disabled={disabled}
          />

          <DatePickerFormItem
            form={form}
            fieldId={FIELD_FULFILLMENT}
            label={locales.payment_fulfillment}
            initialValue={invoice?.fulfillment ? dateUtils.toMomentOrThrow(invoice.fulfillment) : moment()}
            required={true}
            format={MOMENT_DATE_FORMAT}
            rules={fulfillmentRules}
            onChange={this.onDateChanged}
            disabled={disabled}
          />

          <DatePickerFormItem
            form={form}
            fieldId={FIELD_CREATED}
            label={locales.payment_created}
            initialValue={invoice?.createdAt ? dateUtils.toMomentOrThrow(invoice.createdAt) : moment()}
            required={true}
            format={MOMENT_DATE_FORMAT}
            disabled={disabled || !!invoice}
          />

          <DatePickerFormItem
            form={form}
            fieldId={FIELD_DEADLINE}
            label={locales.payment_deadline}
            initialValue={invoice?.deadline ? dateUtils.toMomentOrThrow(invoice.deadline) : moment()}
            required={true}
            format={MOMENT_DATE_FORMAT}
            rules={paymentDeadlineRules}
            onChange={this.onDateChanged}
            disabled={disabled}
          >
            {!disabled &&
                <div>A keltezéshez képest&nbsp;
                    <a onClick={this.addDays(8)}>+8</a>&nbsp;
                    <a onClick={this.addDays(15)}>+15</a>&nbsp;
                    <a onClick={this.addDays(30)}>+30</a>&nbsp;
                    <a onClick={this.addDays(45)}>+45</a> nap
                </div>
            }
          </DatePickerFormItem>

          <CheckBoxFormItem
            form={form}
            fieldId={FIELD_CONTINUOUS_FULFILLMENT}
            initialValue={invoice ? invoice.continuousFulfillment : undefined}
            required={false}
            disabled={disabled}
            label="Folyamatos teljesítésű számla"
            onChange={this.onContinuousFulfillmentCheckChanged}
          />

          <TextAreaFormItem
            form={form}
            fieldId={FIELD_COMMENT}
            label={locales.comment}
            initialValue={invoice ? invoice.comment : commentInitialValue}
            required={false}
            maxLength={255}
            disabled={disabled}
          />
        </Form>

        {this.props.children}

        <SubmitCancelFormItem
          disabled={disabled}
          onSubmit={this.onFormSubmit}
          onCancel={this.onFormCancel}
        />
      </>
    );
  }

  private onClientSelect = (client?: ClientSummary) => {
    const {form} = this.props;
    form.setFieldsValue({[FIELD_CLIENT_ID]: client ? client.id : undefined});
    this.setState({client});
  };

  private onDateChanged = () => {
    // Note: form fieldValues gets set after this callback
    setTimeout(() => {
      this.props.form.validateFields([FIELD_DEADLINE, FIELD_FULFILLMENT]);
    }, 150);
  };

  private onContinuousFulfillmentCheckChanged = (e: CheckboxChangeEvent) => {
    const {form} = this.props;
    const fulfillmentRules = e.target.checked ? [this.continuousFulfillmentDateRule(FIELD_DEADLINE)] : undefined;
    const paymentDeadlineRules = e.target.checked ? [this.continuousFulfillmentDateRule(FIELD_FULFILLMENT)] : undefined;
    this.setState({
      fulfillmentRules, paymentDeadlineRules
    }, () => {
      form.validateFields([FIELD_DEADLINE, FIELD_FULFILLMENT]);
    });
  };

  private continuousFulfillmentDateRule = (otherDateFieldName: string): ValidationRule => {
    const {form: {getFieldValue}} = this.props;
    return {
      validator: (rule, value: moment.Moment, callback) => {
        const other: moment.Moment = getFieldValue(otherDateFieldName);
        if (value && other && !areMomentsEqualToDay(value, other)) {
          callback(locales.continuous_fulfillment_date_error);
        } else {
          callback();
        }
      }
    };
  };

  private addDays = (days: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    const {form} = this.props;
    const created: moment.Moment = moment(form.getFieldValue(FIELD_CREATED));
    const deadline = created.add(days, "days");
    form.setFieldsValue({
      [FIELD_DEADLINE]: deadline,
    });
  };

  private onFormCancel = () => {
    this.props.form.resetFields();
    this.props.onFormCancel();
  };

  private onFormSubmit = () => {
    const {form, onFormSubmit} = this.props;
    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        onFormSubmit({
          clientId: values[FIELD_CLIENT_ID],
          paymentType: values[FIELD_PAYMENT_TYPE],
          created: toIsoString(values[FIELD_CREATED]),
          fulfillment: toIsoString(values[FIELD_FULFILLMENT]),
          deadline: toIsoString(values[FIELD_DEADLINE]),
          comment: values[FIELD_COMMENT],
          continuousFulfillment: values[FIELD_CONTINUOUS_FULFILLMENT]
        });
      }
    });
  };

}

export default Form.create<Props>()(InvoiceForm);



// WEBPACK FOOTER //
// ./src/invoicing/InvoiceForm.tsx